import type { Cents } from "../domain/money";
import { defaultTradeForWork, tradeById, TRADES, type TradeId } from "../domain/trade";
// §76 location thresholds — pure constants; the SAME arithmetic decides
// "throughout home" here and on the bid sheet, without coupling the two.
import { NAMED_ROOM_LIMIT, WHOLE_HOME_MIN_ROOMS, WHOLE_HOME_SHARE_X100 } from "./scopeSummary";

/**
 * THE CATEGORY BUDGET (spec §74 — the two levels of a renovation).
 *
 *     Scope & Estimate  = HOW WE CALCULATED IT   (itemized, per feature)
 *     Category Budget   = HOW WE MANAGE IT       (Demo $8,500, Plumbing $12,500…)
 *
 * The categories ARE the §68 trades — one taxonomy answers "whose sheet
 * does this work go on?" and "which bucket of renovation money is this?".
 * Removal work is DEMOLITION by ACTION whatever is being removed (§74D).
 *
 * Everything here is DERIVED and stored nowhere: the detailed proposed
 * cost lines remain the source data, selected quotes and authorized
 * amounts are stronger planning truths layered over them, and actual
 * money progressively supersedes projection. The PERMANENT precedence:
 *
 *     CALCULATED ESTIMATE → SELECTED QUOTE → AUTHORIZED BUDGET → ACTUAL
 *
 * The same renovation dollar never appears at more than one layer: a
 * quote SUBSTITUTES for the calculated lines it covers (never adds), an
 * authorized amount replaces the quote-adjusted figure for its category,
 * and remaining projection is the current basis MINUS actual spend,
 * floored at zero — a finished category projects its actual and reports
 * variance instead of inventing a remainder.
 *
 * Pure: no I/O, no dates, no randomness. Same inputs, same budget.
 */

export interface CategoryFeatureInput {
  id: string;
  rootId: string;
  featureDefinitionId: string | null;
  name: string;
  action: string;
  roomId: string | null;
  /** 9C work status: 'complete' marks the work finished. */
  workStatus: string | null;
}

export interface CategoryLineInput {
  featureId: string | null;
  amountCents: Cents;
  archived: boolean;
  description: string;
  /** 9C cost basis ('material' | 'labor' | 'other'); null = unstated. */
  costType?: string | null;
}

export interface CategoryQuoteInput {
  id: string;
  contractor: string;
  amountCents: Cents;
  /** Bid scope keys this one price covers (§68/§69). */
  coverage: readonly string[];
  selected: boolean;
}

/** The bid model's scope→features mapping, so quote coverage resolves
 *  through the SAME machinery bids use — never a second claiming rule. */
export interface CategoryScopeInput {
  key: string;
  featureIds: readonly string[];
}

export interface CategoryAuthorizedInput {
  categoryId: string;
  authorizedCents: Cents | null;
  varianceJustification: string;
}

export interface CategoryActualInput {
  sourceId?: string;
  /** Resolved to a feature where the accounting links one (directly or
   *  through its estimate line); null = renovation money with no feature. */
  featureId: string | null;
  amountCents: Cents;
}

export interface CategoryBudgetInput {
  mixedSettlements?: readonly {id:string;description:string;featureIds:readonly string[];sourceId:string;actualCents:Cents}[];
  features: readonly CategoryFeatureInput[];
  /** Per-feature trade overrides, keyed by root id (§68 decisions). */
  decisions: ReadonlyMap<string, TradeId>;
  /** Trade-level combine decisions (§68H): tradeId → quoted with. */
  combines: ReadonlyMap<TradeId, TradeId>;
  lines: readonly CategoryLineInput[];
  quotes: readonly CategoryQuoteInput[];
  scopes: readonly CategoryScopeInput[];
  authorized: readonly CategoryAuthorizedInput[];
  actuals: readonly CategoryActualInput[];
  /** Room id → "Floor 1 → Kitchen" labels for the provenance breakdown. */
  locationLabels: ReadonlyMap<string, string>;
  /** User wording for a management line, by group key — words only (§78R). */
  managementLabels?: ReadonlyMap<string, string>;
}

export type BasisSource = "calculated" | "quoted" | "authorized" | "actual";

export interface CategoryDetailLine {
  /** The work's identity — what a management line groups by (spec §78M). */
  featureDefinitionId: string | null;
  action: string | null;
  roomId: string | null;
  /**
   * The calculated figure split by its own stated cost basis (spec §79M).
   * unclassifiedCents is money whose lines state NO basis — a flat
   * allowance. A split is only ever CLAIMED when unclassified is zero;
   * nothing fabricates a material/labor division an allowance never had.
   */
  materialCents: Cents;
  laborCents: Cents;
  otherCostCents: Cents;
  unclassifiedCents: Cents;
  featureId: string | null;
  name: string;
  locationLabel: string;
  calculatedCents: Cents;
  actualCents: Cents;
}

export interface CategoryBudgetRow {
  /** Aggregate only: unsplit actual is never attributed to member items. */
  mixedSettlementActualCents?: Cents;
  mixedSettlementSourceIds?: string[];
  settledFeatureIds?: string[];
  /** A TradeId, a "+"-joined pair for a combined-quote span, or "unassigned-money". */
  categoryId: string;
  displayName: string;
  sortOrder: number;
  /** Σ of the active detailed estimate lines under this category. */
  calculatedCents: Cents;
  /** The calculated figure with selected quotes substituted for what they cover. */
  quoteAdjustedCents: Cents;
  quotes: Array<{ quoteId: string; contractor: string; amountCents: Cents }>;
  /**
   * THE THREE CHECKPOINTS (spec §84). calculatedCents is PROPOSED — what
   * Berrier Reno estimated BEFORE anyone quoted, and selecting a quote
   * never changes it. quoteCents is what the selected quotes covering this
   * category actually say, each counted ONCE; null means nobody has quoted
   * it, which is not the same fact as a quote of $0. actualCents is the
   * accounting. All three stay independently true; §74's operational
   * precedence (currentBasisCents / basisSource) is untouched.
   */
  quoteCents: Cents | null;
  /** How much of this category's calculated scope those quotes cover. */
  quotedCalculatedCents: Cents;
  quoteCoverage: QuoteCoverage;
  /** Scope identities (features carrying money) covered / present. */
  quotedIdentityCount: number;
  identityCount: number;
  /** quote − proposed. Null when nothing has been quoted — never 0. */
  quoteVarianceCents: Cents | null;
  /** actual − quote. Null without a quote to compare against. */
  actualVsQuoteCents: Cents | null;
  /** actual − proposed: the estimate-to-result comparison. */
  actualVsProposedCents: Cents;
  authorizedCents: Cents | null;
  /** The number the project is currently managed against. */
  currentBasisCents: Cents;
  basisSource: BasisSource;
  actualCents: Cents;
  /** Every feature carrying money is marked complete (9C work status). */
  complete: boolean;
  /** max(0, basis − actual); 0 once complete — never an invented remainder. */
  remainingCents: Cents;
  /** actual + remaining: what this category is expected to cost in the end. */
  projectedCents: Cents;
  /** actual − basis. Meaningful once complete or overspent. */
  varianceCents: Cents;
  varianceJustification: string;
  /** Provenance: the detailed work inside, grouped for display. */
  details: CategoryDetailLine[];
  /**
   * MANAGEMENT WORK ITEMS (spec §78K/L): the same detail, grouped into the
   * handful of lines a project is actually managed by — "Replace Baseboard
   * (15) — throughout home", never fifteen rows. DERIVED by partitioning
   * the details, so the items sum to calculatedCents to the cent; there is
   * no second budget.
   */
  management: ManagementLine[];
  featureIds: string[];
  /** Features a selected quote claims in this row (derived, §88B) — the work the quote's price stands for. */
  claimedFeatureIds: string[];
  /** Recorded actual on those claimed features. */
  claimedActualCents: Cents;
}

export interface CategoryBudgetModel {
  rows: CategoryBudgetRow[];
  totals: {
    calculatedCents: Cents;
    currentBasisCents: Cents;
    actualCents: Cents;
    remainingCents: Cents;
    projectedCents: Cents;
    /** Σ of the selected quotes, each counted once; null when there are none. */
    quoteCents: Cents | null;
    /** The calculated scope those quotes cover — the honest denominator. */
    quotedCalculatedCents: Cents;
    /**
     * Covered ÷ calculated, ×100. "$72,000 quoted" must never read as a
     * quoted renovation when 78% of the scope carries a quote (spec §84-13).
     */
    quoteCoveragePctX100: number | null;
    quoteCoverage: QuoteCoverage;
  };
  /**
   * Σ over open categories of (current basis − calculated): what the
   * stronger planning truths add to (or remove from) the calculated
   * estimate. Phase 12 composes its per-feature projection with exactly
   * this delta, so a selected quote changes the projection ONCE (§74S).
   */
  basisDeltaCents: Cents;
}

/** The category a piece of work belongs to, after every override (§74D). */
export function resolveCategory(
  feature: Pick<CategoryFeatureInput, "featureDefinitionId" | "action" | "rootId">,
  decisions: ReadonlyMap<string, TradeId>,
  combines: ReadonlyMap<TradeId, TradeId>,
): TradeId {
  let tradeId = decisions.get(feature.rootId) ?? defaultTradeForWork(feature.featureDefinitionId, feature.action);
  const combine = combines.get(tradeId);
  if (combine && combine !== tradeId) tradeId = combine;
  return tradeId;
}

/* ------------------------------------------------------------------ *
 * Management work items (spec §78K–O)
 * ------------------------------------------------------------------ */

export interface ManagementLine {
  /** Stable identity: the kind of work + action — never the rendered words. */
  key: string;
  /** The wording the engine derived (kept so a rename can be undone). */
  generatedLabel: string;
  /** What is shown: the user's own wording when they set one (§78R). */
  label: string;
  /** Σ of the member features' calculated lines — partitions the category. */
  amountCents: Cents;
  /** Σ of actuals that RESOLVE to member features; never an allocation. */
  actualCents: Cents;
  /**
   * The calculated figure by stated cost basis (spec §79M). A split is
   * shown only when unclassifiedCents is 0 — an allowance keeps its dash.
   */
  materialCents: Cents;
  laborCents: Cents;
  otherCostCents: Cents;
  unclassifiedCents: Cents;
  featureCount: number;
  /**
   * THE SELECTED QUOTE THAT COVERS EXACTLY THIS ITEM AND NOTHING ELSE
   * (spec §84-9/10). One price covering several items cannot be split
   * between them, so it is reported at the level where it is KNOWN — the
   * category — and every item under it keeps its dash. Null is "no quote",
   * never zero.
   */
  quoteCents: Cents | null;
  quoteContractor: string | null;
  /** Level 3: the underlying detailed scope, for explicit inspection. */
  details: CategoryDetailLine[];
}

/** How much of a category's own scope a selected quote actually covers. */
export type QuoteCoverage = "none" | "partial" | "full";

const roomLeaf = (label: string): string => {
  const arrow = label.lastIndexOf("→");
  return arrow === -1 ? label : label.slice(arrow + 1).trim();
};

/**
 * Group a category's detail lines into management work items (§78L/M/N).
 *
 * Identity is CONCEPTUAL — feature definition + action (falling back to
 * the normalized name for custom work) — never the rendered English, so
 * "Replace Baseboard" in fifteen rooms is one item and crown molding is
 * a different one. Location wording reuses §76's thresholds (the same
 * arithmetic, not the bid presentation). Feature-less manual lines are
 * one explicit item: money never quietly disappears into a residual.
 */
/**
 * THE MANAGEMENT IDENTITY of a piece of work (spec §78M) — the kind of work
 * plus its action, falling back to the normalized name for custom scope, and
 * one explicit bucket for feature-less manual lines. Exported because §91's
 * Completion Review groups by exactly this: a project must not name the same
 * work one way in its budget and another way in its closeout.
 */
export function managementKeyOf(
  detail: Pick<CategoryDetailLine, "featureId" | "featureDefinitionId" | "action" | "name">,
): string {
  return detail.featureId === null
    ? "manual-lines"
    : (detail.featureDefinitionId ?? "name:" + detail.name.trim().toLowerCase()) + "|" + (detail.action ?? "");
}

export function deriveManagementLines(
  details: readonly CategoryDetailLine[],
  totalRooms: number,
  customLabels?: ReadonlyMap<string, string>,
): ManagementLine[] {
  const groups = new Map<string, CategoryDetailLine[]>();
  for (const detail of details) {
    const key = managementKeyOf(detail);
    const group = groups.get(key);
    if (group) group.push(detail);
    else groups.set(key, [detail]);
  }

  const lines: ManagementLine[] = [];
  for (const [key, members] of groups) {
    const amountCents = members.reduce((s, m) => s + m.calculatedCents, 0);
    const actualCents = members.reduce((s, m) => s + m.actualCents, 0);
    const features = members.filter((m) => m.featureId !== null);

    let generatedLabel: string;
    if (key === "manual-lines") {
      generatedLabel =
        members.length === 1 ? members[0].name : "Manual estimate lines (" + members.length + ")";
    } else {
      const base = members[0].name.trim();
      const rooms = [...new Set(members.map((m) => m.roomId).filter((r): r is string => r !== null))];
      let where = "";
      if (rooms.length > 0) {
        if (
          rooms.length >= WHOLE_HOME_MIN_ROOMS &&
          totalRooms > 0 &&
          rooms.length * 100 >= totalRooms * WHOLE_HOME_SHARE_X100
        ) {
          where = " — throughout home";
        } else if (rooms.length <= NAMED_ROOM_LIMIT) {
          const names = [...new Set(members.filter((m) => m.roomId !== null).map((m) => roomLeaf(m.locationLabel)))];
          where = " — " + names.join(", ");
        } else {
          where = " — in " + rooms.length + " rooms";
        }
      }
      generatedLabel = base + (features.length > 1 ? " (" + features.length + ")" : "") + where;
    }

    lines.push({
      key,
      generatedLabel,
      label: customLabels?.get(key) ?? generatedLabel,
      amountCents,
      actualCents,
      materialCents: members.reduce((s, m) => s + m.materialCents, 0),
      laborCents: members.reduce((s, m) => s + m.laborCents, 0),
      otherCostCents: members.reduce((s, m) => s + m.otherCostCents, 0),
      unclassifiedCents: members.reduce((s, m) => s + m.unclassifiedCents, 0),
      featureCount: features.length,
      quoteCents: null,
      quoteContractor: null,
      details: members,
    });
  }
  // Money first; zero-dollar scope after, still visible.
  return lines.sort((a, b) => b.amountCents - a.amountCents || a.generatedLabel.localeCompare(b.generatedLabel));
}

const UNASSIGNED_MONEY = "unassigned-money";

/**
 * A QUOTE BELONGS TO A WORK ITEM ONLY WHEN IT COVERS THAT ITEM AND NOTHING
 * ELSE (spec §84-9/10/35).
 *
 * One plumbing price covering rough-in, fixtures and the water heater is a
 * single fact about all three. Printing $6,400 on each of them would treble
 * it, and splitting it between them would invent an allocation nobody
 * stated. So it stays at the category, where it is actually known, and
 * each item keeps its dash — the same honesty the §79 material/labour
 * split already follows.
 */
function attributeQuotes(
  lines: ManagementLine[],
  claimedBy: ReadonlyMap<string, { quoteId: string; contractor: string; amountCents: Cents }>,
  claimedFeaturesByQuote: ReadonlyMap<string, ReadonlySet<string>>,
): ManagementLine[] {
  for (const line of lines) {
    const featureIds = new Set(
      line.details.map((d) => d.featureId).filter((id): id is string => id !== null),
    );
    const quoteIds = new Set<string>();
    for (const featureId of featureIds) {
      const claim = claimedBy.get(featureId);
      if (claim) quoteIds.add(claim.quoteId);
    }
    if (quoteIds.size !== 1) continue; // none, or a price shared with other items
    const [quoteId] = [...quoteIds];
    const claimed = claimedFeaturesByQuote.get(quoteId);
    if (!claimed) continue;
    // …and it must reach outside this item nowhere.
    let containedHere = true;
    for (const featureId of claimed) {
      if (!featureIds.has(featureId)) {
        containedHere = false;
        break;
      }
    }
    if (!containedHere) continue;
    const claim = claimedBy.get([...featureIds].find((id) => claimedBy.has(id))!)!;
    line.quoteCents = claim.amountCents;
    line.quoteContractor = claim.contractor;
  }
  return lines;
}

export function deriveCategoryBudget(input: CategoryBudgetInput): CategoryBudgetModel {
  const featureById = new Map(input.features.map((f) => [f.id, f]));
  const categoryOf = new Map<string, TradeId>();
  for (const feature of input.features) {
    categoryOf.set(feature.id, resolveCategory(feature, input.decisions, input.combines));
  }

  /* ---- calculated & detail, per feature ---- */
  const calcByFeature = new Map<string, Cents>();
  interface BasisSums { material: Cents; labor: Cents; other: Cents; unclassified: Cents }
  const basisOf = (line: CategoryLineInput, sums: BasisSums) => {
    if (line.costType === "material") sums.material += line.amountCents;
    else if (line.costType === "labor") sums.labor += line.amountCents;
    else if (line.costType === "other") sums.other += line.amountCents;
    else sums.unclassified += line.amountCents;
  };
  const basisByFeature = new Map<string, BasisSums>();
  const featurelessBasis: BasisSums = { material: 0, labor: 0, other: 0, unclassified: 0 };
  let featurelessCalc = 0;
  const featurelessLines: CategoryDetailLine[] = [];
  for (const line of input.lines) {
    if (line.archived) continue;
    if (line.featureId && featureById.has(line.featureId)) {
      calcByFeature.set(line.featureId, (calcByFeature.get(line.featureId) ?? 0) + line.amountCents);
      let sums = basisByFeature.get(line.featureId);
      if (!sums) {
        sums = { material: 0, labor: 0, other: 0, unclassified: 0 };
        basisByFeature.set(line.featureId, sums);
      }
      basisOf(line, sums);
    } else {
      // A manual line with no feature is still scope (spec §41) — it lands
      // in Other / Needs Classification rather than disappearing.
      featurelessCalc += line.amountCents;
      basisOf(line, featurelessBasis);
      featurelessLines.push({
        featureId: null,
        featureDefinitionId: null,
        action: null,
        roomId: null,
        materialCents: line.costType === "material" ? line.amountCents : 0,
        laborCents: line.costType === "labor" ? line.amountCents : 0,
        otherCostCents: line.costType === "other" ? line.amountCents : 0,
        unclassifiedCents: line.costType === "material" || line.costType === "labor" || line.costType === "other" ? 0 : line.amountCents,
        name: line.description || "Manual estimate line",
        locationLabel: "Whole project",
        calculatedCents: line.amountCents,
        actualCents: 0,
      });
    }
  }

  /* ---- actuals, per feature ---- */
  const actualByFeature = new Map<string, Cents>();
  const mixed = input.mixedSettlements ?? [];
  const mixedSourceIds = new Set(mixed.map(s=>s.sourceId));
  let unassignedActual = 0;
  for (const actual of input.actuals) {
    if(actual.sourceId && mixedSourceIds.has(actual.sourceId))continue;
    if (actual.featureId && featureById.has(actual.featureId)) {
      actualByFeature.set(actual.featureId, (actualByFeature.get(actual.featureId) ?? 0) + actual.amountCents);
    } else {
      unassignedActual += actual.amountCents;
    }
  }

  /* ---- selected quotes claim features through the bid scopes (§69) ---- */
  const scopeByKey = new Map(input.scopes.map((s) => [s.key, s]));
  const claimedBy = new Map<string, { quoteId: string; contractor: string; amountCents: Cents }>();
  const quoteClaims: Array<{ quote: CategoryQuoteInput; featureIds: string[]; categories: Set<TradeId> }> = [];
  for (const quote of input.quotes) {
    if (!quote.selected) continue;
    const featureIds: string[] = [];
    const categories = new Set<TradeId>();
    for (const key of quote.coverage) {
      for (const featureId of scopeByKey.get(key)?.featureIds ?? []) {
        if (claimedBy.has(featureId) || !featureById.has(featureId)) continue;
        claimedBy.set(featureId, { quoteId: quote.id, contractor: quote.contractor, amountCents: quote.amountCents });
        featureIds.push(featureId);
        categories.add(categoryOf.get(featureId)!);
      }
    }
    if (featureIds.length > 0) quoteClaims.push({ quote, featureIds, categories });
  }
  /** Every feature one quote claims — how we know it covers ONLY this item. */
  const claimedFeaturesByQuote = new Map<string, Set<string>>(
    quoteClaims.map((claim) => [claim.quote.id, new Set(claim.featureIds)]),
  );

  /* ---- group: one row per category; a multi-category quote MERGES its
   *      span (one price cannot be apportioned, §69) ---- */
  // Union-find-lite: map each TradeId to its group key.
  const groupOf = new Map<TradeId, string>();
  for (const claim of quoteClaims) {
    if (claim.categories.size > 1) {
      const key = [...claim.categories].sort().join("+");
      for (const category of claim.categories) {
        const existing = groupOf.get(category);
        groupOf.set(category, existing && existing !== key ? `${existing}+${key}` : key);
      }
    }
  }
  // One unsplit actual must remain one comparison group, including across trades.
  for(const settlement of mixed) {
    const trades=[...new Set(settlement.featureIds.map(id=>categoryOf.get(id)).filter((t):t is TradeId=>t!==undefined))];
    if(trades.length<2)continue;
    const affected=new Set(trades.map(t=>groupOf.get(t)??t));
    const members=[...new Set([...categoryOf.values()].filter(t=>affected.has(groupOf.get(t)??t)))];
    const key=members.sort().join("+");
    for(const trade of members)groupOf.set(trade,key);
  }

  interface Group {
    key: string;
    trades: Set<TradeId>;
    features: CategoryFeatureInput[];
  }
  const groups = new Map<string, Group>();
  for (const feature of input.features) {
    const trade = categoryOf.get(feature.id)!;
    const key = groupOf.get(trade) ?? trade;
    let group = groups.get(key);
    if (!group) {
      group = { key, trades: new Set(), features: [] };
      groups.set(key, group);
    }
    group.trades.add(trade);
    group.features.push(feature);
  }
  /* Feature-less manual lines land in Other / Needs Classification — and
   * that row must EXIST even when no feature resolves there, or the money
   * silently vanishes from the budget (found by the §78 partition test:
   * an invisible residual is never acceptable). */
  if (featurelessCalc !== 0 && !groups.has("other")) {
    groups.set("other", { key: "other", trades: new Set(["other"]), features: [] });
  }

  const authorizedByCategory = new Map(input.authorized.map((a) => [a.categoryId, a]));

  const rows: CategoryBudgetRow[] = [];
  for (const group of groups.values()) {
    const trades = [...group.trades].sort(
      (a, b) => (tradeById(a)?.sortOrder ?? 999) - (tradeById(b)?.sortOrder ?? 999),
    );
    const displayName =
      trades.length > 1
        ? trades.map((t) => tradeById(t)?.displayName ?? t).join(" + ")
        : group.key === "other"
          ? "Other / Needs Classification"
          : (tradeById(trades[0])?.displayName ?? group.key);
    const sortOrder = tradeById(trades[0])?.sortOrder ?? 999;

    let calculated = 0;
    let actual = 0;
    let claimedCalc = 0;
    let claimedActual = 0;
    const claimedFeatureIds: string[] = [];
    const quoteAmounts = new Map<string, { quoteId: string; contractor: string; amountCents: Cents }>();
    const details: CategoryDetailLine[] = [];
    let anyMoney = false;
    let allComplete = true;
    for (const feature of group.features) {
      const calc = calcByFeature.get(feature.id) ?? 0;
      const spent = actualByFeature.get(feature.id) ?? 0;
      calculated += calc;
      actual += spent;
      if (calc !== 0 || spent !== 0) anyMoney = true;
      if ((calc !== 0 || spent !== 0) && feature.workStatus !== "complete") allComplete = false;
      const claim = claimedBy.get(feature.id);
      if (claim) {
        claimedCalc += calc;
        claimedActual += spent;
        claimedFeatureIds.push(feature.id);
        quoteAmounts.set(claim.quoteId, claim);
      }
      const basis = basisByFeature.get(feature.id) ?? { material: 0, labor: 0, other: 0, unclassified: 0 };
      details.push({
        featureId: feature.id,
        featureDefinitionId: feature.featureDefinitionId,
        action: feature.action,
        roomId: feature.roomId,
        materialCents: basis.material,
        laborCents: basis.labor,
        otherCostCents: basis.other,
        unclassifiedCents: basis.unclassified,
        name: feature.name,
        locationLabel: feature.roomId ? (input.locationLabels.get(feature.roomId) ?? "Whole project") : "Whole project",
        calculatedCents: calc,
        actualCents: spent,
      });
    }
    if (group.key === "other") {
      calculated += featurelessCalc;
      details.push(...featurelessLines);
      if (featurelessCalc !== 0) {
        anyMoney = true;
        allComplete = false;
      }
    }

    const quotes = [...quoteAmounts.values()];
    const quoteTotal = quotes.reduce((s, q) => s + q.amountCents, 0);
    /* COVERAGE IS MEASURED, NEVER ASSUMED (spec §84-11/12/22). An identity
     * is a feature carrying calculated money; a category is fully quoted
     * only when every one of them is claimed. Anything less is PARTIAL and
     * says so, because "$7,500" beside $9,500 of scope otherwise reads as
     * the whole cabinetry quote. */
    let identityCount = 0;
    let quotedIdentityCount = 0;
    for (const feature of group.features) {
      if ((calcByFeature.get(feature.id) ?? 0) === 0) continue;
      identityCount += 1;
      if (claimedBy.has(feature.id)) quotedIdentityCount += 1;
    }
    const quoteCents = quotes.length > 0 ? quoteTotal : null;
    const quoteCoverage: QuoteCoverage =
      quotes.length === 0 ? "none" : quotedIdentityCount >= identityCount ? "full" : "partial";
    // SUBSTITUTION, never addition (§74M): covered work leaves, the quoted
    // price enters — uncovered work in the category keeps its calculation.
    const quoteAdjusted = calculated - claimedCalc + quoteTotal;
    const authorizedRecord =
      trades.length === 1 ? authorizedByCategory.get(trades[0]) : authorizedByCategory.get(group.key);
    const authorized = authorizedRecord?.authorizedCents ?? null;
    const complete = anyMoney && allComplete;

    const basis = authorized ?? quoteAdjusted;
    const groupFeatureIds=new Set(group.features.map(f=>f.id));
    const groupSettlements=mixed.filter(s=>s.featureIds.some(id=>groupFeatureIds.has(id)));
    const settledIds=new Set(groupSettlements.flatMap(s=>[...s.featureIds]));
    const settledActual=groupSettlements.reduce((sum,s)=>sum+s.actualCents,0);
    // All intersecting quoted identities were required in the explicit confirmation.
    const settledCalculated=[...settledIds].reduce((sum,id)=>sum+(calcByFeature.get(id)??0),0);
    const settledQuoteIds=new Set([...settledIds].map(id=>claimedBy.get(id)?.quoteId).filter((id):id is string=>!!id));
    const settledClaimedCalculated=[...settledIds].filter(id=>claimedBy.has(id)).reduce((sum,id)=>sum+(calcByFeature.get(id)??0),0);
    const settledQuote=quotes.filter(q=>settledQuoteIds.has(q.quoteId)).reduce((sum,q)=>sum+q.amountCents,0);
    const settledBasis=authorized!==null && settledIds.size===group.features.length ? basis : settledCalculated-settledClaimedCalculated+settledQuote;
    const openActual=actual;
    actual+=settledActual;
    const basisSource: BasisSource = complete
      ? "actual"
      : authorized !== null
        ? "authorized"
        : quotes.length > 0
          ? "quoted"
          : "calculated";
    const remaining = complete ? 0 : Math.max(0, basis - settledBasis - openActual);

    if (!anyMoney && quotes.length === 0 && authorized === null) continue; // nothing here at all

    rows.push({
      categoryId: group.key,
      mixedSettlementActualCents:settledActual,
      mixedSettlementSourceIds:groupSettlements.map(s=>s.sourceId),
      settledFeatureIds:[...settledIds],
      displayName,
      sortOrder,
      calculatedCents: calculated,
      quoteAdjustedCents: quoteAdjusted,
      quotes,
      quoteCents,
      quotedCalculatedCents: claimedCalc,
      quoteCoverage,
      quotedIdentityCount,
      identityCount,
      quoteVarianceCents: quoteCents === null ? null : quoteCents - calculated,
      actualVsQuoteCents: quoteCents === null ? null : actual - quoteCents,
      actualVsProposedCents: actual - calculated,
      authorizedCents: authorized,
      currentBasisCents: basis,
      basisSource,
      actualCents: actual,
      complete,
      remainingCents: remaining,
      projectedCents: actual + remaining,
      varianceCents: actual - basis,
      varianceJustification: authorizedRecord?.varianceJustification ?? "",
      details,
      management: attributeQuotes(
        deriveManagementLines(details, input.locationLabels.size, input.managementLabels),
        claimedBy,
        claimedFeaturesByQuote,
      ),
      featureIds: group.features.map((f) => f.id),
      claimedFeatureIds,
      claimedActualCents: claimedActual,
    });
  }

  // Renovation money that belongs to no feature at all: its own visible
  // row — never dropped, never guessed into a trade (§74AB).
  if (unassignedActual !== 0) {
    rows.push({
      categoryId: UNASSIGNED_MONEY,
      displayName: "Unassigned money",
      management: [],
      sortOrder: 998,
      calculatedCents: 0,
      quoteAdjustedCents: 0,
      quotes: [],
      quoteCents: null,
      quotedCalculatedCents: 0,
      quoteCoverage: 'none',
      quotedIdentityCount: 0,
      identityCount: 0,
      quoteVarianceCents: null,
      actualVsQuoteCents: null,
      actualVsProposedCents: unassignedActual,
      authorizedCents: null,
      currentBasisCents: 0,
      basisSource: "actual",
      actualCents: unassignedActual,
      complete: false,
      remainingCents: 0,
      projectedCents: unassignedActual,
      varianceCents: unassignedActual,
      varianceJustification: "",
      details: [],
      featureIds: [],
      claimedFeatureIds: [],
      claimedActualCents: 0,
    });
  }

  rows.sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName));

  /* PROJECT-LEVEL COVERAGE (spec §84-13): what fraction of the calculated
   * renovation carries a selected quote. Without it, a quote total reads as
   * though the whole job had been priced. */
  const quoted = rows.filter((r) => r.quoteCents !== null);
  const calculatedAll = rows.reduce((s, r) => s + r.calculatedCents, 0);
  const coveredAll = rows.reduce((s, r) => s + r.quotedCalculatedCents, 0);
  const coveragePctX100 = calculatedAll === 0 ? null : Math.round((coveredAll * 10000) / calculatedAll);
  const projectCoverage: QuoteCoverage =
    quoted.length === 0 ? 'none' : rows.every((r) => r.quoteCoverage === 'full' || r.identityCount === 0) ? 'full' : 'partial';
  const totals = {
    calculatedCents: calculatedAll,
    currentBasisCents: rows.reduce((s, r) => s + r.currentBasisCents, 0),
    actualCents: rows.reduce((s, r) => s + r.actualCents, 0),
    remainingCents: rows.reduce((s, r) => s + r.remainingCents, 0),
    projectedCents: rows.reduce((s, r) => s + r.projectedCents, 0),
    /* A quote spanning several trades MERGES them into one row, so every
     * selected quote lives in exactly one row and summing rows counts each
     * once (spec §84-20, test-pinned). */
    quoteCents: quoted.length === 0 ? null : quoted.reduce((s, r) => s + (r.quoteCents ?? 0), 0),
    quotedCalculatedCents: rows.reduce((s, r) => s + r.quotedCalculatedCents, 0),
    quoteCoveragePctX100: coveragePctX100,
    quoteCoverage: projectCoverage,
  };
  // What the stronger truths change vs the calculation, over OPEN work only
  // (a finished category's projection is its actual already).
  const basisDeltaCents = rows
    .filter((r) => !r.complete)
    .reduce((s, r) => s + (r.currentBasisCents - r.calculatedCents), 0);

  return { rows, totals, basisDeltaCents };
}

/** Every category the taxonomy offers, for pickers and empty states. */
export function allBudgetCategories(): Array<{ id: TradeId; displayName: string; sortOrder: number }> {
  return TRADES.filter((t) => t.id !== "owner").map((t) => ({
    id: t.id,
    displayName: t.id === "other" ? "Other / Needs Classification" : t.displayName,
    sortOrder: t.sortOrder,
  }));
}

/**
 * THE PROJECTION'S CATEGORY ADJUSTMENT, COUNTED ONCE (spec §74S, corrected
 * in §88B).
 *
 * Phase 12 projects each feature per cost basis (actual-plus-remaining), so
 * a flooring labor deposit above that feature's labor estimate already
 * raises its projection. When a selected quote covers that flooring, the
 * deposit is part of the quoted price — adding the quote's delta on top
 * counted the overrun twice (found in the §88B customer run: an $800 deposit
 * against a $400 labor estimate inside a $3,100 quote projected flooring at
 * $3,500).
 *
 * So for every open category whose basis is not the calculation, the work
 * that basis stands for is REPLACED rather than stacked: the claimed
 * features' own projection drift (projected − proposed) is taken back out,
 * and the price projects as max(price, actual recorded against it). A
 * quote replaces only the features it claims; an authorized budget replaces
 * the whole category. With nothing recorded the drift is zero and this is
 * exactly `basisDeltaCents` — every figure without actuals is unchanged.
 */
export function projectionBasisAdjustment(
  model: CategoryBudgetModel,
  projectedLines: ReadonlyArray<{ featureId: string | null; proposedCents: Cents; projectedCents: Cents }>,
): Cents {
  const drift = new Map<string, Cents>();
  for (const line of projectedLines) {
    if (line.featureId === null) continue;
    drift.set(line.featureId, (drift.get(line.featureId) ?? 0) + (line.projectedCents - line.proposedCents));
  }
  const driftOf = (ids: readonly string[]) => ids.reduce((sum, id) => sum + (drift.get(id) ?? 0), 0);
  let adjustment = 0;
  for (const row of model.rows) {
    if(row.mixedSettlementActualCents) {
      // Phase12 already carries the group's actual beside zero projected member bases.
      // This also preserves separately attributed deposits when physical
      // completion changes the row's basis source to Actual.
      adjustment += row.projectedCents-row.calculatedCents-driftOf(row.featureIds)-row.mixedSettlementActualCents;
      continue;
    }
    if (row.complete) continue;
    if (row.basisSource === "authorized") {
      adjustment += row.projectedCents - row.calculatedCents - driftOf(row.featureIds);
    } else if (row.basisSource === "quoted") {
      const price = row.quoteCents ?? 0;
      adjustment += Math.max(price, row.claimedActualCents) - row.quotedCalculatedCents - driftOf(row.claimedFeatureIds);
    }
  }
  return adjustment;
}
